import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ShieldAlert } from "lucide-react";

const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 dakika hareketsizlik
const WARNING_SECONDS = 10; // son 10 saniye uyarı

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);
  const idleTimer = useRef(null);
  const countdown = useRef(null);
  const warningRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdown.current) clearInterval(countdown.current);
  }, []);

  const doLogout = useCallback(async () => {
    clearTimers();
    warningRef.current = false;
    setShowWarning(false);
    try { await logout(); } catch (e) { /* yine de yönlendir */ }
    navigate("/login", { replace: true });
  }, [clearTimers, logout, navigate]);

  const startWarning = useCallback(() => {
    warningRef.current = true;
    setSecondsLeft(WARNING_SECONDS);
    setShowWarning(true);
    countdown.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(countdown.current);
          doLogout();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [doLogout]);

  const resetIdle = useCallback(() => {
    if (warningRef.current) return; // uyarı açıkken aktivite sıfırlamaz, butona basılmalı
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(startWarning, IDLE_LIMIT_MS - WARNING_SECONDS * 1000);
  }, [startWarning]);

  const keepAlive = useCallback(async () => {
    if (countdown.current) clearInterval(countdown.current);
    warningRef.current = false;
    setShowWarning(false);
    try { await api.post("/auth/refresh"); } catch (e) { /* token yenileme denemesi */ }
    resetIdle();
  }, [resetIdle]);

  useEffect(() => {
    if (!user || user === true) {
      clearTimers();
      return;
    }
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const onActivity = () => resetIdle();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimers();
    };
  }, [user, resetIdle, clearTimers]);

  if (!showWarning || !user || user === true) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="idle-warning-overlay"
    >
      <div className="bg-white border border-[#E7DECC] rounded-sm max-w-md w-[92%] p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-sm bg-[#A63A3A]/10 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5 text-[#A63A3A]" />
          </div>
          <h2 className="font-heading font-bold text-lg text-[#26231E]">Oturumunuz sonlanmak üzere</h2>
        </div>
        <p className="text-sm text-[#8C8377] mb-1">
          Uzun süredir işlem yapmadınız. Güvenliğiniz için oturumunuz kapatılacak.
        </p>
        <p className="text-sm text-[#8C8377] mb-5">
          <span className="font-heading font-bold text-2xl text-[#A63A3A] tabular-nums" data-testid="idle-countdown">{secondsLeft}</span>{" "}
          saniye içinde giriş ekranına yönlendirileceksiniz.
        </p>
        <div className="flex gap-3">
          <button
            onClick={keepAlive}
            data-testid="idle-keep-alive-btn"
            className="flex-1 bg-[#26231E] text-white font-medium text-sm py-2.5 rounded-sm hover:bg-[#1a1a1a] transition-colors duration-150"
          >
            Oturumu Açık Tut
          </button>
          <button
            onClick={doLogout}
            data-testid="idle-logout-btn"
            className="px-4 border border-[#E7DECC] text-[#8C8377] font-medium text-sm py-2.5 rounded-sm hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            Çıkış Yap
          </button>
        </div>
      </div>
    </div>
  );
}
